// feat_408 — SOPORTE-F4b: "Crear manual" sobre una brecha, con confirmación.
// Muestra el prompt de SOLO documentación que generó la brecha (editable). Al confirmar queda
// el pedido registrado (brecha "manual en cola") a nombre de quien confirma.
// ⚠ El backend no puede escribir en /home/info/waia-prompts ni encolar en la cola de
// WAIA-Full (corre en un container sin ese acceso): el prompt se entrega al servidor desde el
// conector (F5) o a mano con "Copiar prompt". La pantalla lo dice claro.
import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useMediaQuery, useTheme,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { errorText } from "./shared";

export const GAP_STATUS_LABELS = {
  abierta: "Abierta",
  manual_en_cola: "Manual pedido",
  manual_disponible: "Manual disponible",
  cerrada: "Cerrada",
};
export const DELIVERY_LABELS = {
  no_pedido: "Sin pedir",
  pendiente_servidor: "Pedido · falta encolar en el servidor",
  encolado: "Encolado en el servidor",
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Prompt copiado.");
  } catch {
    toast.error("No se pudo copiar (el navegador lo bloqueó). Seleccioná el texto y copialo a mano.");
  }
};

const ManualDialog = ({ gapId, open, onClose, onDone }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setResult(null);
    api.get(`/superadmin/support/gaps/${gapId}`)
      .then(({ data: d }) => { setData(d); setText(d.promptText || ""); })
      .catch((err) => { toast.error(errorText(err)); onClose(); });
    // onClose suele ser una flecha inline del padre: NO va en las deps (refetch en loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gapId]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/superadmin/support/gaps/${gapId}/request-manual`, { promptText: text });
      setResult(d);
      toast.success(d.requested ? "Pedido de manual registrado." : "Esta brecha ya tenía un pedido de manual.");
      onDone && onDone(d);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const gap = data?.gap;
  const alreadyRequested = gap && gap.status !== "abierta";
  const shownText = result?.promptText || (alreadyRequested ? data.promptText : text);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen} data-manual-dialog={gapId}>
      <DialogTitle>Crear manual · brecha #{gapId}</DialogTitle>
      <DialogContent dividers>
        {!data ? (
          <CircularProgress />
        ) : (
          <>
            <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <Chip size="small" label={GAP_STATUS_LABELS[gap.status] || gap.status} />
              <Chip size="small" variant="outlined" label={`${gap.affectedCount} consulta(s)`} />
              <Chip size="small" variant="outlined" label={DELIVERY_LABELS[result?.delivery || gap.delivery] || gap.delivery} />
            </Box>
            <Typography variant="subtitle2">{gap.topic}</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>{gap.description}</Typography>
            <Typography variant="body2" paragraph>
              Prompt de <strong>solo documentación</strong> para Claude Code: lee el código y las features del módulo y escribe la sección del manual,
              sin tocar código. Nombre: <code>{result?.promptName || data.promptName}</code> → <code>{data.promptPath}</code> (cola <code>{data.queueRepo}</code>).
            </Typography>
            <Box style={{ background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 6, padding: 8, marginBottom: 8 }} data-manual-server-note>
              <Typography variant="body2">
                El servidor de WAIA no puede escribir el archivo ni encolarlo solo. Al confirmar queda el pedido registrado (brecha «Manual pedido»);
                para lanzarlo, pedíselo a Claude desde el chat (herramienta del conector) o copiá el prompt y encolalo a mano.
              </Typography>
            </Box>
            <TextField
              label="Texto del prompt" variant="outlined" fullWidth multiline minRows={14} maxRows={28}
              value={shownText}
              onChange={(e) => { const v = e.target.value; setText(v); }}
              InputProps={{ readOnly: !!(result || alreadyRequested) }}
              inputProps={{ "data-manual-text": true, style: { fontFamily: "monospace", fontSize: 13 } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {data && <Button onClick={() => copy(shownText)} data-manual-copy>Copiar prompt</Button>}
        <Button onClick={onClose} disabled={busy}>{result || alreadyRequested ? "Cerrar" : "Cancelar"}</Button>
        {data && !alreadyRequested && !result && (
          <Button variant="contained" color="primary" disabled={busy || !text.trim()} onClick={confirm} data-manual-confirm>
            {busy ? "Registrando…" : "Confirmar pedido de manual"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManualDialog;
